import { useState, useMemo } from "react";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { QuestionnairePage, ResultPanel } from "./SBCQualifier";

const QUESTIONS = [
    {
        id: "compulsory",
        question: "In the past 12 months, has your taxable supplies turnover EXCEEDED R2.3 million? (Or do you reasonably expect it to in the next 12 months under a written contract?)",
        helper: "VAT registration is COMPULSORY when the R2.3m threshold is or will be breached. This threshold was increased from R1 million in the 2026 Budget, effective 1 April 2026.",
        options: [
            { label: "Yes — over R2.3m past 12 months", value: "yes_actual" },
            { label: "Yes — written contract guarantees it in the next 12 months", value: "yes_future" },
            { label: "No — under R2.3m and unlikely to breach soon", value: "no" },
        ],
        autoResolve: (v) =>
            v === "yes_actual"
                ? { kind: "fail", reason: "You MUST register for VAT. Section 23(1) requires registration within 21 business days of breaching R2.3m. Failure to register triggers SARS penalties and unrecoverable VAT on past supplies." }
                : v === "yes_future"
                ? { kind: "fail", reason: "Section 23(1)(b) requires compulsory registration where a written contract guarantees a breach of R2.3m within the next 12 months. Register before raising the first invoice on the contract." }
                : null,
    },
    {
        id: "voluntary",
        question: "Has your taxable turnover EXCEEDED R120,000 in the past 12 months?",
        helper: "Voluntary registration is allowed once you cross R120,000 in taxable supplies (raised from R50,000 in the 2026 Budget, effective 1 April 2026).",
        options: [
            { label: "Yes — over R120,000", value: "yes" },
            { label: "No — under R120,000", value: "no" },
        ],
        evaluate: (v) =>
            v === "no"
                ? { kind: "pass", reason: "You don't yet meet the R120,000 voluntary registration threshold. Continue to track your monthly turnover and re-test once you cross R120k." }
                : { kind: "ok" },
    },
    {
        id: "customers",
        question: "Most of your customers are…",
        helper: "Who you invoice changes whether VAT helps or hurts your competitiveness.",
        options: [
            { label: "VAT-registered businesses (B2B)", value: "b2b" },
            { label: "Private consumers / end-users (B2C)", value: "b2c" },
            { label: "Mix of both", value: "mixed" },
        ],
        evaluate: (v) =>
            v === "b2c"
                ? { kind: "maybe", reason: "Most of your customers are end-users who can't claim back VAT. Registering will effectively make you 15% more expensive — unless you can absorb the VAT into your price. Consider waiting." }
                : { kind: "ok" },
    },
    {
        id: "input",
        question: "Do you incur substantial input VAT each month — rent, equipment, professional fees, raw materials?",
        helper: "Registering lets you claim that input VAT back from SARS.",
        options: [
            { label: "Yes — material input VAT every month", value: "yes" },
            { label: "Some, but not material", value: "some" },
            { label: "Very little", value: "no" },
        ],
        evaluate: (v) =>
            v === "yes"
                ? { kind: "ok" }
                : v === "no"
                ? { kind: "maybe", reason: "If you have very little input VAT to recover, registration is mostly admin overhead. The case for registering is weaker." }
                : { kind: "ok" },
    },
    {
        id: "admin",
        question: "Are you ready for the bi-monthly admin? (VAT201 returns, supporting documents, valid tax invoices, SARS verifications)",
        helper: "VAT compliance is real work. Most TCA clients let us run it for them, but it is still your responsibility.",
        options: [
            { label: "Yes — happy to run it (or have TCA do it)", value: "yes" },
            { label: "Concerned about the admin load", value: "concerned" },
        ],
        evaluate: (v) =>
            v === "concerned"
                ? { kind: "maybe", reason: "VAT non-compliance carries 10% penalties + interest + understatement penalties. If admin load is your biggest worry, our Core or Elite packages handle the bi-monthly cycle for you." }
                : { kind: "ok" },
    },
];

export default function VATRegistrationDecision() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [autoResult, setAutoResult] = useState(null);

    const total = QUESTIONS.length;
    const current = QUESTIONS[step];
    const finished = step >= total;

    const result = useMemo(() => {
        if (autoResult) return autoResult;
        if (!finished) return null;
        const evals = QUESTIONS.map((q) => (answers[q.id] !== undefined ? q.evaluate?.(answers[q.id]) : null));
        const passes = evals.filter((e) => e && e.kind === "pass");
        const fails = evals.filter((e) => e && e.kind === "fail");
        const maybes = evals.filter((e) => e && e.kind === "maybe");
        if (fails.length) return { kind: "fail", details: fails };
        if (passes.length) return { kind: "pass", details: passes };
        if (maybes.length) return { kind: "maybe", details: maybes };
        return { kind: "pass", details: [] };
    }, [answers, finished, autoResult]);

    const choose = (val) => {
        setAnswers((a) => ({ ...a, [current.id]: val }));
        if (current.autoResolve) {
            const auto = current.autoResolve(val);
            if (auto) {
                setAutoResult({ kind: auto.kind, details: [auto] });
                setStep(total);
                return;
            }
        }
        setStep((s) => s + 1);
    };

    const reset = () => {
        setAnswers({});
        setAutoResult(null);
        setStep(0);
    };

    return (
        <QuestionnairePage
            title="Should You Register for VAT?"
            subtitle="VAT Act 89 of 1991 · Sections 7, 23 and 24"
            current={step}
            total={total}
            onReset={reset}
            testIdRoot="vat"
        >
            {!finished && current && (
                <div className="space-y-7" data-testid={`vat-question-${step}`}>
                    <div>
                        <p className="overline text-tca-orange mb-3">Question {step + 1} of {total}</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight">{current.question}</h2>
                        {current.helper && (
                            <p className="mt-3 text-sm text-tca-mute leading-relaxed flex items-start gap-2">
                                <Info className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" /> {current.helper}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2.5">
                        {current.options.map((o) => (
                            <button
                                key={o.label}
                                onClick={() => choose(o.value)}
                                data-testid={`vat-option-${step}-${o.value}`}
                                className="w-full text-left bg-white border border-tca-line hover:border-tca-orange hover:bg-tca-orangeSoft/50 transition-all px-5 py-4 group flex items-center justify-between gap-3"
                            >
                                <span className="font-medium text-tca-ink">{o.label}</span>
                                <ArrowRight className="w-4 h-4 text-tca-mute group-hover:text-tca-orange transition-colors" />
                            </button>
                        ))}
                    </div>
                    {step > 0 && (
                        <button onClick={() => setStep(step - 1)} data-testid={`vat-back-${step}`} className="btn-ghost">
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                    )}
                </div>
            )}

            {finished && result && (
                <ResultPanel
                    result={result}
                    passTitle="Voluntary registration looks net-positive"
                    passBody="Your business profile suggests VAT registration is the right move — you can claim input VAT, your B2B customers can claim it back, and the admin load is manageable."
                    failTitle="VAT registration is required (or you are at risk)"
                    failBody="The legislation pushes you into the VAT net here:"
                    maybeTitle="Mixed signals — let's run the numbers together"
                    maybeBody="The case for voluntary registration is not clear-cut for your business. Some pointers:"
                    onReset={reset}
                />
            )}
        </QuestionnairePage>
    );
}
